import React, { useEffect, useState } from "react";
import { apiGet, apiPost } from "lib/apiClient";

/**
 * Pulling market questions from an external source, in the partner portal.
 *
 * Same pipeline the admin portal uses and deliberately the same shape: a source is
 * fetched, what comes back lands in a review queue as candidates, and a human decides
 * which of them becomes a market. Nothing here can publish anything — an approved
 * candidate becomes a DRAFT market, which still has to go through submit and approval
 * like any other.
 *
 * A partner can fetch from the sources their tenant already has and review what comes
 * back. They cannot create or edit a source: a source carries an endpoint the server
 * will call, and that is the one part of ingestion that can be aimed at a private
 * network, so configuring it stays with the admins.
 */
export default function ExternalSources({ onCandidateConverted }) {
  const [sources, setSources] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  async function load() {
    try {
      const [sourceRes, candidateRes] = await Promise.all([
        apiGet("/portal/ingestion/sources"),
        apiGet("/portal/ingestion/candidates?status=PENDING&pageSize=50"),
      ]);
      setSources(sourceRes?.sources ?? []);
      setCandidates(candidateRes?.candidates ?? []);
      setError(null);
    } catch (e) {
      setError(e.message || "Could not load sources.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function fetchNow(source) {
    setBusyId(source.id);
    setError(null);
    setNotice(null);
    try {
      const result = await apiPost(`/portal/ingestion/sources/${source.id}/fetch`, {});
      const found = result?.candidates?.length ?? 0;
      setNotice(
        found > 0
          ? `${source.displayName}: ${found} question${found === 1 ? "" : "s"} for review.`
          : `${source.displayName}: nothing new.`,
      );
      await load();
    } catch (e) {
      // A provider being unreachable is a fact about the provider, not a failure of the
      // page — the rest of the sources stay usable.
      setError(e.message || `Could not fetch from ${source.displayName}.`);
    } finally {
      setBusyId(null);
    }
  }

  if (loading) return <p className="text-sm text-gray-500">Loading sources…</p>;

  return (
    <div className="space-y-6">
      <section>
        <h3 className="text-base font-semibold">Sources</h3>
        {sources.length === 0 ? (
          <p className="text-sm text-gray-500">
            No sources are configured for your account yet. An administrator sets these up.
          </p>
        ) : (
          <ul className="mt-2 divide-y divide-gray-200 dark:divide-navy-700">
            {sources.map((source) => (
              <li key={source.id} className="flex items-center justify-between gap-3 py-2">
                <div>
                  <p className="text-sm font-medium">{source.displayName}</p>
                  <p className="text-xs text-gray-500">
                    {source.providerType}
                    {source.enabled ? "" : " · disabled"}
                    {source.lastSuccessAt ? ` · last fetched ${new Date(source.lastSuccessAt).toLocaleString()}` : " · never fetched"}
                  </p>
                </div>
                <button
                  type="button"
                  className="rounded-sm bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50"
                  disabled={!source.enabled || busyId === source.id}
                  onClick={() => fetchNow(source)}
                >
                  {busyId === source.id ? "Fetching…" : "Fetch now"}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h3 className="text-base font-semibold">For review</h3>
        {candidates.length === 0 ? (
          <p className="text-sm text-gray-500">Nothing waiting. Fetch from a source to bring questions in.</p>
        ) : (
          <ul className="mt-2 space-y-2">
            {candidates.map((candidate) => (
              <li key={candidate.id} className="rounded-lg border border-gray-200 p-3 dark:border-navy-700">
                <p className="text-sm font-medium">{candidate.question}</p>
                <p className="mt-1 text-xs text-gray-500">
                  {candidate.source?.name ?? "Unknown source"}
                  {candidate.closesAt ? ` · closes ${new Date(candidate.closesAt).toLocaleDateString()}` : ""}
                </p>
                {candidate.suggestedClusters?.length > 0 && (
                  <p className="mt-1 flex flex-wrap items-center gap-1 text-xs text-gray-500">
                    <span>Looks like</span>
                    {candidate.suggestedClusters.map((s) => (
                      <span
                        key={s.clusterKey}
                        title={`Matched: ${s.matched.join(", ")}`}
                        className="rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-700 dark:bg-navy-700 dark:text-gray-200"
                      >
                        {s.clusterKey}
                      </span>
                    ))}
                  </p>
                )}
                {!candidate.resolutionCriteria && (
                  // The RSS adapter leaves criteria empty on purpose: a headline is not
                  // a question that can settle, and conversion refuses without them.
                  <p className="mt-1 text-xs text-amber-600">
                    Needs resolution criteria before it can become a market.
                  </p>
                )}
                <button
                  type="button"
                  className="mt-2 rounded-sm border border-gray-300 px-3 py-1 text-xs dark:border-navy-600"
                  onClick={() => onCandidateConverted?.(candidate)}
                >
                  Use this question
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {notice && <p className="text-sm text-green-600">{notice}</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}
